import { useSettings } from "../../context/SettingsContext";
import {
    Toggle,
    SettingRow,
    SectionCard,
    NumberInput,
    ButtonGroup,
} from "../ui/index";

const PAGINATION_TYPES = [
    { value: "thumbs", label: "Thumbnails" },
    { value: "dots", label: "Dots" },
];

const LIGHTBOX_TRANSITIONS = [
    { value: "slide", label: "Slide" },
    { value: "fade", label: "Fade" },
    { value: "zoom", label: "Zoom" },
    { value: "none", label: "None" },
];

export default function LightboxPanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);
    const enabled = settings.lightbox;

    return (
        <div className="space-y-5">
            <SectionCard
                title="Lightbox"
                description="Full-screen image viewer overlay."
            >
                <SettingRow
                    label="Enable Lightbox"
                    description="Allow product images to be opened in a full-screen lightbox."
                >
                    <Toggle
                        value={settings.lightbox}
                        onChange={(v) => s("lightbox", v)}
                    />
                </SettingRow>
            </SectionCard>

            {enabled && (
                <>
                    <SectionCard
                        title="Opening Behaviour"
                        description="How and when the lightbox is triggered."
                    >
                        <SettingRow
                            label="Show Expand Button"
                            description="Display a fullscreen icon button on the main image."
                        >
                            <Toggle
                                value={settings.lightboxOnClick}
                                onChange={(v) => s("lightboxOnClick", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Open on Image Click"
                            description="Also open the lightbox when clicking the main product image directly."
                        >
                            <Toggle
                                value={settings.lightboxOpenOnImageClick}
                                onChange={(v) =>
                                    s("lightboxOpenOnImageClick", v)
                                }
                            />
                        </SettingRow>

                        <SettingRow
                            label="Close on Overlay Click"
                            description="Close the lightbox when clicking the dark background overlay."
                        >
                            <Toggle
                                value={settings.lightboxCloseOnOverlay}
                                onChange={(v) => s("lightboxCloseOnOverlay", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Keyboard Navigation"
                            description="Use arrow keys to navigate and Escape to close the lightbox."
                        >
                            <Toggle
                                value={settings.lightboxKeyboard}
                                onChange={(v) => s("lightboxKeyboard", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Swipe / Drag Navigation"
                            description="Drag or swipe the lightbox image to navigate between images. Works on desktop (mouse drag) and mobile (touch swipe)."
                        >
                            <Toggle
                                value={settings.lightboxSwipe}
                                onChange={(v) => s("lightboxSwipe", v)}
                            />
                        </SettingRow>
                    </SectionCard>

                    <SectionCard
                        title="Display"
                        description="What elements are shown inside the lightbox."
                    >
                        <SettingRow
                            label="Single Image Mode"
                            description="Show only the clicked image in the lightbox instead of the full gallery."
                        >
                            <Toggle
                                value={settings.lightboxSingleImage}
                                onChange={(v) => s("lightboxSingleImage", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Navigation Arrows"
                            description="Show previous and next arrow buttons for navigating between images."
                        >
                            <Toggle
                                value={settings.lightboxShowArrows}
                                onChange={(v) => s("lightboxShowArrows", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Loop Images"
                            description="Loop back to the first image after reaching the last."
                        >
                            <Toggle
                                value={settings.lightboxLoop}
                                onChange={(v) => s("lightboxLoop", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Image Counter"
                            description='Show current position indicator (e.g. "2 / 5").'
                        >
                            <Toggle
                                value={settings.lightboxShowCounter}
                                onChange={(v) => s("lightboxShowCounter", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Image Caption"
                            description="Show the image alt text as a caption below the image."
                        >
                            <Toggle
                                value={settings.lightboxShowCaption}
                                onChange={(v) => s("lightboxShowCaption", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Pagination"
                            description="Show navigation indicators below the lightbox image."
                        >
                            <Toggle
                                value={settings.lightboxShowThumbnails}
                                onChange={(v) => s("lightboxShowThumbnails", v)}
                            />
                        </SettingRow>

                        {settings.lightboxShowThumbnails && (
                            <SettingRow
                                label="Pagination Style"
                                description="Display as thumbnail images or simple dot indicators."
                            >
                                <ButtonGroup
                                    options={PAGINATION_TYPES}
                                    value={settings.lightboxPaginationType}
                                    onChange={(v) =>
                                        s("lightboxPaginationType", v)
                                    }
                                />
                            </SettingRow>
                        )}

                        {settings.lightboxShowThumbnails &&
                            settings.lightboxPaginationType === "thumbs" && (
                                <SettingRow
                                    label="Thumbnail Radius"
                                    description="Rounded corner radius of thumbnail strip items."
                                >
                                    <NumberInput
                                        value={
                                            settings.lightboxThumbBorderRadius
                                        }
                                        onChange={(v) =>
                                            s("lightboxThumbBorderRadius", v)
                                        }
                                        min={0}
                                        max={50}
                                        unit="px"
                                    />
                                </SettingRow>
                            )}

                        <SettingRow
                            label="Image Radius"
                            description="Rounded corner radius of the main lightbox image."
                        >
                            <NumberInput
                                value={settings.lightboxImageBorderRadius}
                                onChange={(v) =>
                                    s("lightboxImageBorderRadius", v)
                                }
                                min={0}
                                max={80}
                                unit="px"
                            />
                        </SettingRow>
                    </SectionCard>

                    <SectionCard
                        title="Toolbar Buttons"
                        description="Buttons shown in the lightbox toolbar."
                    >
                        <SettingRow
                            label="Zoom In / Out"
                            description="Show zoom in and zoom out buttons in the lightbox toolbar."
                        >
                            <Toggle
                                value={settings.lightboxZoomIn}
                                onChange={(v) => s("lightboxZoomIn", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Mouse Scroll Zoom"
                            description="Allow scrolling the mouse wheel to zoom in and out on the lightbox image."
                        >
                            <Toggle
                                value={settings.lightboxScrollZoom}
                                onChange={(v) => s("lightboxScrollZoom", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Autoplay Button"
                            description="Show the slideshow play/pause toggle button in the toolbar."
                        >
                            <Toggle
                                value={settings.lightboxShowAutoplayButton}
                                onChange={(v) =>
                                    s("lightboxShowAutoplayButton", v)
                                }
                            />
                        </SettingRow>

                        <SettingRow
                            label="Fullscreen"
                            description="Show the fullscreen toggle button."
                        >
                            <Toggle
                                value={settings.lightboxFullscreen}
                                onChange={(v) => s("lightboxFullscreen", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Image Tools"
                            description="Show the center toolbar with rotate, flip, and 1:1 zoom buttons."
                        >
                            <Toggle
                                value={settings.lightboxImageTools}
                                onChange={(v) => s("lightboxImageTools", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Share Button"
                            description="Show a share button in the toolbar that opens a menu with sharing and download options."
                        >
                            <Toggle
                                value={settings.lightboxShare}
                                onChange={(v) => s("lightboxShare", v)}
                            />
                        </SettingRow>

                        {settings.lightboxShare && (
                            <>
                                <SettingRow
                                    label="Share on Facebook"
                                    description="Include 'Share on Facebook' in the share menu."
                                >
                                    <Toggle
                                        value={settings.lightboxShareFacebook}
                                        onChange={(v) =>
                                            s("lightboxShareFacebook", v)
                                        }
                                    />
                                </SettingRow>

                                <SettingRow
                                    label="Tweet"
                                    description="Include 'Tweet' (Twitter/X) in the share menu."
                                >
                                    <Toggle
                                        value={settings.lightboxShareTwitter}
                                        onChange={(v) =>
                                            s("lightboxShareTwitter", v)
                                        }
                                    />
                                </SettingRow>

                                <SettingRow
                                    label="Pin it"
                                    description="Include 'Pin it' (Pinterest) in the share menu."
                                >
                                    <Toggle
                                        value={settings.lightboxSharePinterest}
                                        onChange={(v) =>
                                            s("lightboxSharePinterest", v)
                                        }
                                    />
                                </SettingRow>

                                <SettingRow
                                    label="Download Image"
                                    description="Include 'Download image' in the share menu."
                                >
                                    <Toggle
                                        value={settings.lightboxShareDownload}
                                        onChange={(v) =>
                                            s("lightboxShareDownload", v)
                                        }
                                    />
                                </SettingRow>
                            </>
                        )}
                    </SectionCard>

                    <SectionCard
                        title="Animation"
                        description="Transition effect and speed for lightbox."
                    >
                        <SettingRow
                            label="Effect"
                            description="Animation style when navigating between images."
                        >
                            <ButtonGroup
                                options={LIGHTBOX_TRANSITIONS}
                                value={settings.lightboxTransition}
                                onChange={(v) => s("lightboxTransition", v)}
                            />
                        </SettingRow>

                        {settings.lightboxTransition !== "none" && (
                            <SettingRow
                                label="Duration"
                                description="Length of the open/close and navigation animations."
                            >
                                <NumberInput
                                    value={settings.lightboxAnimationDuration}
                                    onChange={(v) =>
                                        s("lightboxAnimationDuration", v)
                                    }
                                    min={0}
                                    max={2000}
                                    unit="ms"
                                    width="w-24"
                                />
                            </SettingRow>
                        )}

                        <SettingRow
                            label="Zoom From Image"
                            description="Animate the lightbox opening and closing by zooming from/to the source image position."
                        >
                            <Toggle
                                value={settings.lightboxZoomOrigin}
                                onChange={(v) => s("lightboxZoomOrigin", v)}
                            />
                        </SettingRow>
                    </SectionCard>

                    <SectionCard
                        title="Slideshow"
                        description="Auto-advance through images automatically."
                    >
                        <SettingRow
                            label="Enable Autoplay"
                            description="Automatically advance to the next image at a set interval."
                        >
                            <Toggle
                                value={settings.lightboxAutoplay}
                                onChange={(v) => s("lightboxAutoplay", v)}
                            />
                        </SettingRow>

                        {settings.lightboxAutoplay && (
                            <SettingRow
                                label="Slide Interval"
                                description="Time between slides during autoplay."
                            >
                                <NumberInput
                                    value={settings.lightboxAutoplayDelay}
                                    onChange={(v) =>
                                        s("lightboxAutoplayDelay", v)
                                    }
                                    min={500}
                                    max={30000}
                                    unit="ms"
                                    width="w-24"
                                />
                            </SettingRow>
                        )}
                    </SectionCard>
                </>
            )}
        </div>
    );
}
